import type { MouseEvent, PointerEvent, RefObject, ReactNode } from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { computePanelBottomAboveChrome } from "#/components/game/gameCanvas/boardViewport";
import { useGameCanvasOverlayLayout } from "#/components/game/gameCanvas/overlayLayoutContext";
import { suppressGhostInteractionAtPoint } from "#/components/game/suppressGhostInteractionAfterDismiss";
import { useMediaQuery } from "#/hooks/useMediaQuery";
import { cn } from "#/lib/utils";

const CHROME_GAP_PX = 8;
const DESKTOP_MEDIA = "(min-width: 640px)";

function readOverlayContainer(
  overlayRootRef: RefObject<HTMLDivElement | null>,
): HTMLElement | null {
  const root = overlayRootRef.current;
  if (!root?.firstElementChild || !(root.firstElementChild instanceof HTMLElement)) {
    return null;
  }
  return root.firstElementChild;
}

function measureDockedPanelLayout({
  overlayRootRef,
  canvasRef,
  panelRef,
  bottomDockRef,
}: {
  overlayRootRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  bottomDockRef: RefObject<HTMLElement | null>;
}): { bottom: number } {
  const container = readOverlayContainer(overlayRootRef);
  const canvas = canvasRef.current;
  const panel = panelRef.current;
  const dock = bottomDockRef.current;
  if (!container || !canvas || !panel) {
    return { bottom: CHROME_GAP_PX };
  }

  const containerRect = container.getBoundingClientRect();
  const dockTop = dock?.getBoundingClientRect().top ?? containerRect.bottom;
  return {
    bottom: computePanelBottomAboveChrome({
      containerBottom: containerRect.bottom,
      dockTop,
      gap: CHROME_GAP_PX,
    }),
  };
}

function useDockedPanelLayout({
  bottomDockRef,
  panelRef,
}: {
  bottomDockRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
}) {
  const { overlayRootRef, canvasRef, viewportRef } = useGameCanvasOverlayLayout();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA, true);
  const [layout, setLayout] = useState({ bottom: CHROME_GAP_PX });

  const updateLayout = useCallback(() => {
    setLayout(
      measureDockedPanelLayout({
        overlayRootRef,
        canvasRef,
        panelRef,
        bottomDockRef,
      }),
    );
  }, [bottomDockRef, canvasRef, overlayRootRef, panelRef]);

  useLayoutEffect(() => {
    updateLayout();
    const container = readOverlayContainer(overlayRootRef);
    const observer = new ResizeObserver(updateLayout);
    if (container) observer.observe(container);
    if (canvasRef.current) observer.observe(canvasRef.current);
    if (panelRef.current) observer.observe(panelRef.current);
    if (bottomDockRef.current) observer.observe(bottomDockRef.current);
    const viewport = viewportRef.current;
    viewport?.addEventListener("scroll", updateLayout, { passive: true });
    window.addEventListener("resize", updateLayout);
    window.visualViewport?.addEventListener("resize", updateLayout);
    window.visualViewport?.addEventListener("scroll", updateLayout);
    return () => {
      observer.disconnect();
      viewport?.removeEventListener("scroll", updateLayout);
      window.removeEventListener("resize", updateLayout);
      window.visualViewport?.removeEventListener("resize", updateLayout);
      window.visualViewport?.removeEventListener("scroll", updateLayout);
    };
  }, [bottomDockRef, canvasRef, overlayRootRef, panelRef, updateLayout, viewportRef]);

  return { layout, isDesktop };
}

const PANEL_INTERACTIVE_SELECTOR =
  'button, a, input, label, [role="checkbox"], [data-slot="checkbox"], [data-slot="label"]';

function isPanelInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(PANEL_INTERACTIVE_SELECTOR));
}

function useRejectStrayPanelPointerEvents() {
  const panelPointerDownRef = useRef(false);

  return {
    onPanelPointerDownCapture: () => {
      panelPointerDownRef.current = true;
    },
    onPanelPointerUpCapture: (event: PointerEvent<HTMLDivElement>) => {
      if (panelPointerDownRef.current || isPanelInteractiveTarget(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    },
    onPanelClickCapture: (event: MouseEvent<HTMLDivElement>) => {
      if (isPanelInteractiveTarget(event.target)) {
        queueMicrotask(() => {
          panelPointerDownRef.current = false;
        });
        return;
      }
      const startedOnPanel = panelPointerDownRef.current;
      if (startedOnPanel || event.detail === 0) {
        queueMicrotask(() => {
          panelPointerDownRef.current = false;
        });
        return;
      }
      panelPointerDownRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}

function useBackdropDismiss(onCancel: () => void) {
  const backdropPointerDownRef = useRef(false);

  return {
    onBackdropPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      if (event.target !== event.currentTarget) return;
      backdropPointerDownRef.current = true;
    },
    onBackdropPointerUp: (event: PointerEvent<HTMLButtonElement>) => {
      if (!backdropPointerDownRef.current) return;
      backdropPointerDownRef.current = false;
      if (event.target !== event.currentTarget) return;
      event.preventDefault();
      event.stopPropagation();
      suppressGhostInteractionAtPoint({
        clientX: event.clientX,
        clientY: event.clientY,
      });
      onCancel();
    },
    onBackdropPointerCancel: () => {
      backdropPointerDownRef.current = false;
    },
    onBackdropPointerLeave: () => {
      backdropPointerDownRef.current = false;
    },
  };
}

const gameDockedPanelOverlayClassName =
  "pointer-events-auto fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs animate-in fade-in-0";

export const gameDockedPanelSurfaceClassName =
  "pointer-events-auto z-20 grid gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 outline-none";

export const gameDockedPanelTitleClassName = "font-heading text-base leading-none font-medium";

export const gameDockedPanelFooterClassName =
  "-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end";

function readDockedPanelLayout({
  isCompact,
  isDesktop,
  layout,
}: {
  isCompact: boolean;
  isDesktop: boolean;
  layout: { bottom: number };
}) {
  if (!isDesktop && !isCompact) {
    return { bottom: layout.bottom, left: 0, right: 0 };
  }
  return { bottom: layout.bottom, left: 0, right: 0, marginInline: "auto" };
}

function dockedPanelShellClassName({
  isCompact,
  isDesktop,
}: {
  isCompact: boolean;
  isDesktop: boolean;
}) {
  return cn(
    "pointer-events-auto absolute z-50 animate-in fade-in-0 slide-in-from-bottom-4 duration-100",
    isCompact
      ? "w-fit max-w-[calc(100%-1.5rem)] px-0"
      : "w-full max-w-none px-3 sm:w-sm sm:max-w-[min(24rem,calc(100%-2rem))] sm:px-0",
    (isDesktop || isCompact) && "mx-auto",
  );
}

export function GameDockedPanelShell({
  bottomDockRef,
  onCancel,
  closeAriaLabel = "Close panel",
  panelWidth = "wide",
  modal = true,
  children,
}: {
  bottomDockRef: RefObject<HTMLElement | null>;
  onCancel: () => void;
  closeAriaLabel?: string;
  panelWidth?: "wide" | "fit";
  modal?: boolean;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { layout, isDesktop } = useDockedPanelLayout({ bottomDockRef, panelRef });
  const backdropDismiss = useBackdropDismiss(onCancel);
  const rejectStrayPointerEvents = useRejectStrayPanelPointerEvents();
  const isCompact = panelWidth === "fit";

  return (
    <>
      {modal ? (
        <button
          type="button"
          aria-label={closeAriaLabel}
          className={gameDockedPanelOverlayClassName}
          onPointerDown={backdropDismiss.onBackdropPointerDown}
          onPointerUp={backdropDismiss.onBackdropPointerUp}
          onPointerCancel={backdropDismiss.onBackdropPointerCancel}
          onPointerLeave={backdropDismiss.onBackdropPointerLeave}
        />
      ) : null}
      <div
        ref={panelRef}
        className={dockedPanelShellClassName({ isCompact, isDesktop })}
        style={readDockedPanelLayout({ isCompact, isDesktop, layout })}
        onPointerDownCapture={rejectStrayPointerEvents.onPanelPointerDownCapture}
        onPointerUpCapture={rejectStrayPointerEvents.onPanelPointerUpCapture}
        onClickCapture={rejectStrayPointerEvents.onPanelClickCapture}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </>
  );
}
